/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { ActivityIndicator, Linking, Pressable, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/colors'
import { spacing } from '../../shared/theme/spacing';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { modalExperience } from '../../shared/modals/modalExperience.styles'
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader'
import { OperationalConfirmBody } from '../../shared/modals/OperationalConfirmModal';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { formatMoney } from '../../shared/format/public-api';
import { MercadoPagoQrCode } from './MercadoPagoQrCode';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { StatusPill } from '../../shared/surface-cards/StatusPill';
import { formatSaleShortRefLabel } from '../../sales/application/salePresentation';
import { MP_QR_AUTO_CLOSE_MS, MP_QR_TTL_SECONDS, shouldShowMpQrCloseConfirm } from '../domain/mpQrPaymentPhase'
import type { MpQrPaymentPhase } from '../domain/mpQrPaymentPhase';
import { mercadoPagoQrModalStyles as mpStyles } from './mercadoPagoQrModal.styles';

export type MercadoPagoQrPaymentModalProps = {
  visible: boolean;
  /** 'sale' = cobro de venta POS; 'customer_payment' = cobro de cuenta corriente. */
  variant?: 'sale' | 'customer_payment';
  phase: MpQrPaymentPhase;
  loading: boolean;
  error: string | null;
  qrData: string | null;
  qrUrl: string | null;
  amount: number | null;
  saleRef: string | null;
  expiresAt: string | null;
  countdownSeconds: number | null;
  fiscalInvoiceLabel: string | null;
  autoCloseProgress: number | null;
  lastMpQrSaleId: string | null;
  onClose: () => void;
  onRegenerate?: () => void;
  onOpenSale?: () => void;
  onVoidSale?: () => void;
  onConfigureMercadoPago?: () => void;
  onChangePaymentMethod?: () => void;
  /** variant='customer_payment': cancela el intent de cobro QR. */
  onCancelIntent?: () => void;
};

function formatCountdown(totalSeconds: number | null): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds) || totalSeconds < 0) return '';
  return new Intl.DateTimeFormat('es-AR', { minute: 'numeric', second: '2-digit', timeZone: 'UTC' }).format(
    new Date(totalSeconds * 1000)
  );
}

export function MercadoPagoQrPaymentModal(props: MercadoPagoQrPaymentModalProps) {
  const {
    visible,
    variant = 'sale',
    phase,
    loading,
    error,
    qrData,
    qrUrl,
    amount,
    saleRef,
    countdownSeconds,
    fiscalInvoiceLabel,
    autoCloseProgress,
    lastMpQrSaleId,
    onClose,
    onRegenerate,
    onOpenSale,
    onVoidSale,
    onConfigureMercadoPago,
    onChangePaymentMethod,
    onCancelIntent,
  } = props;

  const modalBackdropPadding = useModalBackdropPadding();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const insets = useSafeAreaInsets();
  const formatAmount = (value: number | null) =>
    value == null || !Number.isFinite(value) ? '' : formatMoney(value);
  const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);

  useEffect(() => {
    if (!visible) setCloseConfirmVisible(false);
  }, [visible]);

  useEffect(() => {
    if (!visible || phase !== 'approved') return;
    const timer = setTimeout(() => onClose(), MP_QR_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [visible, phase, onClose]);

  const handleRequestClose = useCallback(() => {
    if (shouldShowMpQrCloseConfirm(phase)) {
      setCloseConfirmVisible(true);
      return;
    }
    onClose();
  }, [onClose, phase]);

  const showGenerating = loading || phase === 'generating';
  const showWaiting = !showGenerating && phase === 'waiting_scan' && (qrData || qrUrl);
  const showApproved = phase === 'approved';
  const showExpired = phase === 'expired';
  const showRejected = phase === 'rejected';
  const showError = phase === 'error' || (phase === 'voided' && error);

  const countdownPct =
    countdownSeconds != null && countdownSeconds > 0
      ? Math.min(100, Math.max(0, (countdownSeconds / MP_QR_TTL_SECONDS) * 100))
      : 0;

  return (
    <Modal visible={visible} transparent animationType={isMobile ? 'slide' : 'fade'} onRequestClose={handleRequestClose}>
        <Pressable
          style={[posSharedUi.modalBackdrop, isMobile ? { justifyContent: 'flex-end', padding: 0, alignItems: 'stretch' } : [modalBackdropPadding, modalExperience.modalRoot]]}
          onPress={handleRequestClose}
        >
          <Pressable
            style={[isMobile ? [mpStyles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm) }] : [posSharedUi.modalCard, mpStyles.modalCard, modalExperience.modalWidthStandard]]}
            onPress={(event) => event.stopPropagation()}
          >
            {isMobile ? <View style={mpStyles.grabber} /> : null}
          <View style={isMobile ? mpStyles.sheetHeader : undefined}>
            <TicketModalHeader
              kicker={variant === 'customer_payment' ? 'Clientes' : 'Ventas'}
              title="QR Mercado Pago"
              subtitle={saleRef ? `Venta ${saleRef}` : undefined}
              onClose={handleRequestClose}
              compact={isMobile}
            />
          </View>

          <View style={isMobile ? mpStyles.sheetBody : undefined}>
          {closeConfirmVisible ? (
            <>
              <OperationalConfirmBody
                title={phase === 'generating' ? '¿Salir mientras generamos el QR?' : '¿Salir del cobro QR?'}
                message={
                  variant === 'customer_payment'
                    ? phase === 'generating'
                      ? 'Todavía estamos generando el QR. Podés seguir esperando o salir.'
                      : 'El cobro sigue pendiente de Mercado Pago. Si el cliente ya pagó, se acredita solo. Si salís sin cancelar, el QR sigue vivo unos minutos.'
                    : phase === 'generating'
                      ? 'Todavía estamos generando el QR. Podés seguir esperando o retomarlo después desde el POS o Ventas.'
                      : 'La venta ya está anotada y el stock ya salió. Podés seguir esperando el pago. También la retomás después desde Ventas. Si hace falta, cambiá el medio o anulala para devolver el stock.'
                }
                iconName="alert-circle-outline"
              />
              <View style={mpStyles.closeConfirmActions}>
                {onVoidSale ? (
                  <TouchableOpacity
                    style={modalExperience.dangerButton}
                    onPress={() => {
                      setCloseConfirmVisible(false);
                      onVoidSale();
                    }}
                  >
                    <Text style={modalExperience.dangerButtonText}>Anular venta</Text>
                  </TouchableOpacity>
                ) : null}
                {variant === 'customer_payment' && onCancelIntent ? (
                  <TouchableOpacity
                    style={modalExperience.dangerButton}
                    onPress={() => {
                      setCloseConfirmVisible(false);
                      onCancelIntent();
                    }}
                  >
                    <Text style={modalExperience.dangerButtonText}>Cancelar cobro QR</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  style={mpStyles.primaryButton}
                  onPress={() => setCloseConfirmVisible(false)}
                >
                  <Text style={mpStyles.primaryButtonText}>Seguir esperando</Text>
                </TouchableOpacity>
                {onChangePaymentMethod ? (
                  <TouchableOpacity
                    style={mpStyles.secondaryButton}
                    onPress={() => {
                      setCloseConfirmVisible(false);
                      onChangePaymentMethod();
                    }}
                  >
                    <Text style={mpStyles.secondaryButtonText}>Cambiar medio de pago</Text>
                  </TouchableOpacity>
                ) : null}
                <TouchableOpacity
                  style={mpStyles.secondaryButton}
                  onPress={() => {
                    setCloseConfirmVisible(false);
                    onClose();
                  }}
                >
                  <Text style={mpStyles.secondaryButtonText}>
                    {variant === 'customer_payment' ? 'Cerrar y esperar el pago' : 'Cerrar y retomar después'}
                  </Text>
                </TouchableOpacity>
              </View>
            </>
          ) : (
            <>
              {saleRef ? (
                <Text style={[mpStyles.saleRef, mpStyles.contentFullWidth]}>Venta #{saleRef}</Text>
              ) : null}

              {showGenerating ? (
                <ModalStatePanel
                  loading
                  loadingMessage={
                    variant === 'customer_payment'
                      ? 'Generando el QR…'
                      : 'Generando el QR. Sincronizando la venta…'
                  }
                />
              ) : null}

              {showApproved ? (
                <View style={mpStyles.successBlock}>
                  <Ionicons name="checkmark-circle" size={56} color={colors.success} />
                  <Text style={posSharedUi.modalTitle}>Cobro confirmado</Text>
                  {amount != null ? <Text style={mpStyles.amountHero}>{formatAmount(amount)}</Text> : null}
                  <StatusPill tone="success" label="Pagado" />
                  {fiscalInvoiceLabel ? <Text style={mpStyles.fiscalChip}>{fiscalInvoiceLabel}</Text> : null}
                  <Text style={posSharedUi.caption}>Cerrando en unos segundos…</Text>
                  {autoCloseProgress != null ? (
                    <View style={mpStyles.autoCloseTrack}>
                      <View style={[mpStyles.autoCloseFill, { width: `${autoCloseProgress * 100}%` }]} />
                    </View>
                  ) : null}
                </View>
              ) : null}

              {(showExpired || showRejected) ? (
                <View style={mpStyles.warningPanel}>
                  <Text style={mpStyles.warningTitle}>{showExpired ? 'QR vencido' : 'Cobro no confirmado'}</Text>
                  <Text style={mpStyles.warningBody}>
                    {showExpired
                      ? variant === 'customer_payment'
                        ? 'El cliente no pagó a tiempo. Generá un QR nuevo o cancelá el cobro.'
                        : 'El cliente no pagó a tiempo. Generá un QR nuevo o anulá la venta para devolver el stock. Si corresponde, sale nota de crédito.'
                      : 'Mercado Pago rechazó o canceló el cobro. Generá un QR nuevo para reintentar.'}
                  </Text>
                  {fiscalInvoiceLabel ? (
                    <Text style={mpStyles.fiscalChip}>{fiscalInvoiceLabel} · Cobro pendiente</Text>
                  ) : null}
                  {onRegenerate ? (
                    <TouchableOpacity style={mpStyles.primaryButton} onPress={onRegenerate}>
                      <Text style={mpStyles.primaryButtonText}>Generar nuevo QR</Text>
                    </TouchableOpacity>
                  ) : null}
                  {onVoidSale ? (
                    <TouchableOpacity style={mpStyles.secondaryButton} onPress={onVoidSale}>
                      <Text style={mpStyles.secondaryButtonText}>Anular venta</Text>
                    </TouchableOpacity>
                  ) : null}
                  {variant === 'customer_payment' && onCancelIntent ? (
                    <TouchableOpacity style={mpStyles.secondaryButton} onPress={onCancelIntent}>
                      <Text style={mpStyles.secondaryButtonText}>Cancelar cobro</Text>
                    </TouchableOpacity>
                  ) : null}
                  {onOpenSale ? (
                    <TouchableOpacity style={mpStyles.secondaryButton} onPress={onOpenSale}>
                      <Text style={mpStyles.secondaryButtonText}>Ver en Ventas</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              ) : null}

              {showError ? (
                <View style={mpStyles.errorBlock}>
                  <Text style={posSharedUi.caption}>{error ?? 'No se pudo completar el cobro QR.'}</Text>
                  {lastMpQrSaleId ? (
                    <Text style={[posSharedUi.caption, { opacity: 0.75 }]}>
                      Venta registrada · Ref. {formatSaleShortRefLabel(lastMpQrSaleId)}
                    </Text>
                  ) : null}
                  <View style={mpStyles.actionStack}>
                    {onRegenerate ? (
                      <TouchableOpacity style={mpStyles.primaryButton} onPress={onRegenerate} disabled={loading}>
                        {loading ? (
                          <ActivityIndicator size="small" color="#FFFFFF" />
                        ) : (
                          <Text style={mpStyles.primaryButtonText}>Generar QR de nuevo</Text>
                        )}
                      </TouchableOpacity>
                    ) : null}
                    {onVoidSale ? (
                      <TouchableOpacity style={modalExperience.dangerButton} onPress={onVoidSale}>
                        <Text style={modalExperience.dangerButtonText}>Anular venta</Text>
                      </TouchableOpacity>
                    ) : null}
                    {onChangePaymentMethod ? (
                      <TouchableOpacity style={mpStyles.secondaryButton} onPress={onChangePaymentMethod}>
                        <Text style={mpStyles.secondaryButtonText}>Cambiar medio de pago</Text>
                      </TouchableOpacity>
                    ) : null}
                    {onOpenSale ? (
                      <TouchableOpacity style={mpStyles.secondaryButton} onPress={onOpenSale}>
                        <Text style={mpStyles.secondaryButtonText}>Ver en Ventas</Text>
                      </TouchableOpacity>
                    ) : null}
                    {onConfigureMercadoPago ? (
                      <TouchableOpacity style={mpStyles.secondaryButton} onPress={onConfigureMercadoPago}>
                        <Text style={mpStyles.secondaryButtonText}>Configurar Mercado Pago</Text>
                      </TouchableOpacity>
                    ) : null}
                  </View>
                </View>
              ) : null}

              {showWaiting ? (
                <View style={mpStyles.successBlock}>
                  <Text style={mpStyles.hint}>
                    Abrí Mercado Pago → <Text style={mpStyles.hintStrong}>Pagar</Text> →{' '}
                    <Text style={mpStyles.hintStrong}>Código QR</Text> → escaneá esta pantalla.
                  </Text>
                  <MercadoPagoQrCode value={qrData ?? qrUrl ?? ''} />
                  <View style={mpStyles.statusRow}>
                    <StatusPill tone="warning" label="Esperando pago" />
                    {amount != null ? <Text style={mpStyles.amountHero}>{formatAmount(amount)}</Text> : null}
                  </View>
                  {fiscalInvoiceLabel ? (
                    <Text style={mpStyles.fiscalChip}>{fiscalInvoiceLabel} · Cobro pendiente</Text>
                  ) : null}
                  {countdownSeconds != null ? (
                    <>
                      <View style={mpStyles.countdownBarTrack}>
                        <View style={[mpStyles.countdownBarFill, { width: `${countdownPct}%` }]} />
                      </View>
                      <Text style={mpStyles.countdownLabel}>Tiempo restante {formatCountdown(countdownSeconds)}</Text>
                    </>
                  ) : null}
                  <View style={{ marginTop: spacing.xs, alignItems: 'center' }}>
                    <ActivityIndicator size="small" color={colors.accent} />
                    <Text style={[posSharedUi.caption, { marginTop: spacing.xs }]}>
                      Esperando confirmación de Mercado Pago…
                    </Text>
                  </View>
                </View>
              ) : null}

              {!showGenerating && !showApproved && !showExpired && !showRejected && !showError && !showWaiting && qrUrl ? (
                <View style={mpStyles.successBlock}>
                  <MercadoPagoQrCode value={qrUrl} />
                  <TouchableOpacity
                    style={[posSharedUi.secondaryButton, { marginTop: spacing.sm }]}
                    onPress={() => Linking.openURL(qrUrl)}
                  >
                    <Text style={posSharedUi.secondaryButtonText}>Abrir en el navegador</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
            </>
          )}
          </View>
        </Pressable>
        </Pressable>
    </Modal>
  );
}
